import React from 'react';

export interface TableProps {
  children: React.ReactNode;
  variant?: 'glassmorphism' | 'elevated';
  className?: string;
}

export interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export interface TableRowProps {
  children: React.ReactNode;
  hover?: boolean;
  className?: string;
  onClick?: () => void;
}

export interface TableCellProps {
  children: React.ReactNode;
  header?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

/**
 * Table Component v2 - FacturXPro Design System
 *
 * Table avec effet glassmorphism et hover effects
 * Composition de plusieurs composants: Table, TableHeader, TableBody, TableRow, TableCell
 *
 * @example
 * ```tsx
 * <Table variant="glassmorphism">
 *   <TableHeader>
 *     <TableRow>
 *       <TableCell header>Nom</TableCell>
 *       <TableCell header>Email</TableCell>
 *       <TableCell header align="right">Montant</TableCell>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     <TableRow hover>
 *       <TableCell>John Doe</TableCell>
 *       <TableCell>john@example.com</TableCell>
 *       <TableCell align="right">€1,250</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Tables"
 */
export const Table: React.FC<TableProps> = ({
  children,
  variant = 'glassmorphism',
  className = '',
}) => {
  const variantClasses = {
    glassmorphism:
      'bg-white/20 backdrop-blur-xl border border-white/30 shadow-xl shadow-gray-300/10',
    elevated: 'bg-white border border-gray-200 shadow-md',
  };

  return (
    <div
      className={`rounded-3xl overflow-hidden ${variantClasses[variant]} ${className}`}
    >
      <div className="overflow-x-auto">
        <table className="w-full">{children}</table>
      </div>
    </div>
  );
};

export const TableHeader: React.FC<TableHeaderProps> = ({
  children,
  className = '',
}) => {
  return (
    <thead className={`bg-white/40 border-b border-gray-200/50 ${className}`}>
      {children}
    </thead>
  );
};

export const TableBody: React.FC<TableBodyProps> = ({
  children,
  className = '',
}) => {
  return <tbody className={className}>{children}</tbody>;
};

export const TableRow: React.FC<TableRowProps> = ({
  children,
  hover = false,
  className = '',
  onClick,
}) => {
  const hoverClasses = hover
    ? 'hover:bg-white/60 transition-colors duration-200 cursor-pointer'
    : '';

  return (
    <tr
      className={`border-b border-gray-200/30 last:border-0 ${hoverClasses} ${className}`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
};

export const TableCell: React.FC<TableCellProps> = ({
  children,
  header = false,
  align = 'left',
  className = '',
}) => {
  const Component = header ? 'th' : 'td';

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const baseClasses = header
    ? 'px-6 py-4 text-xs font-bold text-gray-700 uppercase tracking-wider'
    : 'px-6 py-4 text-sm text-gray-900';

  return (
    <Component className={`${baseClasses} ${alignClasses[align]} ${className}`}>
      {children}
    </Component>
  );
};

export default Table;
